import { useState } from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { Business, Delete, Folder } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_ACCESSES } from '../user.graphql';
import { GET_ACCESSES_FOR_USER } from '../../pdf/pdf.graphql';
import { RemoveUserAccessDialog } from '../../licenses/details/RemoveUserAccessDialog';
import { UserAccessHistoric } from './UserAccessHistoric';
import { formatDateOnly } from '../../Formatters';
import { License, UserAccess } from '../interfaces';
import { tokens } from './tokens';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Props {
  userId: number;
}

interface SingleDoc {
  id: number;
  docName: string;
  title: string;
  validFrom: string;
  validTo: string;
}

export function AccessesPanel({ userId }: Props) {
  const accesses = useQuery<{ userAccess: UserAccess[] }>(GET_ACCESSES, { variables: { id: userId } });
  const singleDocs = useQuery<{ accesses: SingleDoc[] }>(GET_ACCESSES_FOR_USER, { variables: { userId } });
  const [toRemove, setToRemove] = useState<UserAccess | undefined>(undefined);

  if (accesses.error) return <Box sx={{ p: 3 }}><span>Feil! {accesses.error.message}</span></Box>;

  const grouped = accesses.data ? groupByLicense(accesses.data.userAccess) : [];
  const docs = singleDocs.data?.accesses ?? [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {toRemove && (
        <RemoveUserAccessDialog
          displayName={toRemove.subscription.name}
          userId={userId}
          userAccessId={toRemove.id}
          licenseId={toRemove.license.id}
          onClose={() => setToRemove(undefined)}
        />
      )}

      <Typography sx={{ fontSize: 13, color: tokens.textSecondary, mb: 2 }}>
        Aktive abonnementstilganger gruppert per lisens
      </Typography>

      {accesses.loading && !accesses.data && <LoadingSpinner />}

      {!accesses.loading && grouped.length === 0 && docs.length === 0 && (
        <Typography variant="caption" color="textDisabled">Har ingen tilganger</Typography>
      )}

      {grouped.map((group) => (
        <Box
          key={group.license.id}
          sx={{
            mb: 2,
            border: `1px solid ${tokens.border}`,
            borderRadius: `${tokens.radius}px`,
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: tokens.surfaceAlt,
              borderBottom: `1px solid ${tokens.border}`,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Business sx={{ fontSize: 18, color: tokens.primary }} />
              <Typography sx={{ fontSize: 13, color: tokens.textSecondary }}>gjennom</Typography>
              <Box
                component={Link}
                to={`/license/${group.license.id}`}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: tokens.primary,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}>
                {group.license.companyName || '<Mangler navn>'}
              </Box>
            </Box>
            <Chip
              size="small"
              label={group.accessList.length === 1 ? '1 tilgang' : `${group.accessList.length} tilganger`}
              sx={{ background: '#EEF1F3', color: tokens.textSecondary, fontWeight: 500, height: 22, borderRadius: 999 }}
            />
          </Box>
          <Box>
            {group.accessList.map((item, i) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  padding: '12px 16px',
                  borderTop: i === 0 ? 'none' : `1px solid ${tokens.divider}`,
                }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: tokens.success, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, color: tokens.text, fontWeight: 500 }}>
                    {item.subscription.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.textSecondary, mt: 0.25 }}>
                    fra {formatDateOnly(item.createdAt)}
                  </Typography>
                </Box>
                <IconButton size="small" title="Fjern tilgang" onClick={() => setToRemove(item)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      {docs.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: tokens.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mt: 3,
              mb: 1,
            }}>
            Enkeltdokument-tilganger
          </Typography>
          <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: `${tokens.radius}px`, overflow: 'hidden' }}>
            {docs.map((d, i) => (
              <Box
                key={d.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  padding: '12px 16px',
                  borderTop: i === 0 ? 'none' : `1px solid ${tokens.divider}`,
                }}>
                <Folder sx={{ fontSize: 18, color: tokens.textSecondary }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: tokens.text }}>
                    <Box component="span" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', color: tokens.textSecondary, mr: 1 }}>
                      {d.docName}
                    </Box>
                    {d.title}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.textSecondary, mt: 0.25 }}>
                    fra {formatDateOnly(d.validFrom)} til {formatDateOnly(d.validTo)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Box sx={{ mt: 3 }}>
        <UserAccessHistoric userId={userId} />
      </Box>
    </Box>
  );
}

function groupByLicense(accessList: UserAccess[]): { license: License; accessList: UserAccess[] }[] {
  const grouped = accessList
    .filter((x) => !x.isDeleted)
    .reduce((g: Record<number, UserAccess[]>, item) => {
      g[item.license.id] = g[item.license.id] || [];
      g[item.license.id].push(item);
      return g;
    }, {});
  return Object.values(grouped).map((list) => ({ license: list[0].license, accessList: list }));
}
